import { UseNavigateResult } from "@tanstack/react-router";
import { User } from "oidc-client-ts";

import { prefetchAccess } from "~/access/payload";
import {
  type AuthProfile,
  authProfileAtom,
  orgsAtom,
  store,
  userAtom,
  zitadelOrgAtom,
} from "~/atoms";
import {
  createAxiosRequest,
  decodeSuccessTransformer,
} from "~/hooks/useApiQuery";
import {
  actAfterLoginSuccess,
  navigateToMainEntryPageIfSingleOrg,
} from "~/utils/Helper";
import { LooseObject, Org } from "~/utils/Types";

// ---------------- API wrappers ----------------
// Thin createAxiosRequest calls with no hook dependency, shared by the boot
// sequence (useAppBootstrap) and applyOrgSelection below.

export async function fetchUserOrgs(args: {
  user: User | null;
  zitadelOrgIdString?: string;
}): Promise<Org[]> {
  return createAxiosRequest<Org[]>({
    config: { method: "post", data: {} },
    path: "/user/UserOrgs",
    transformers: [decodeSuccessTransformer],
    user: args.user ? { access_token: args.user.access_token } : null,
    zitadelOrg: args.zitadelOrgIdString
      ? { idString: args.zitadelOrgIdString }
      : null,
  });
}

export async function fetchUserConfig(args: {
  user: User | null;
  zitadelOrgIdString?: string;
}): Promise<LooseObject> {
  return createAxiosRequest<LooseObject>({
    config: { method: "post", data: {} },
    path: "/user/UserConfig",
    transformers: [decodeSuccessTransformer],
    user: args.user ? { access_token: args.user.access_token } : null,
    zitadelOrg: args.zitadelOrgIdString
      ? { idString: args.zitadelOrgIdString }
      : null,
  });
}

export function profileFromZitadelUser(user: User): AuthProfile {
  const profile = user.profile ?? {};
  const firstName = (profile.given_name as string | undefined) ?? "";
  const lastName = (profile.family_name as string | undefined) ?? "";
  const displayName =
    (profile.name as string | undefined) ?? `${firstName} ${lastName}`.trim();
  return {
    id: (profile.sub as string | undefined) ?? "",
    email: (profile.email as string | undefined) ?? "",
    firstName,
    lastName,
    displayName,
    mongoId: profile.mongoId as string | undefined,
  };
}

export interface ApplyOrgSelectionArgs {
  orgIdString: string;
  orgs: Org[];
  navigate?: UseNavigateResult<string>;
  // Boot re-fetches UserConfig (behaviour-preserving default); a Nav switch
  // does not. /user/UserConfig is org-invariant, so a switch would refetch a
  // guaranteed-identical response.
  shouldRefetchUserConfig?: boolean;
}

/**
 * Path-agnostic org selection: set the picked org current, fetch the access
 * payload for the picked place, refresh the session atoms, and update the
 * display profile. Reads the acting user / zitadelOrg from the jotai store
 * (the single source of truth) rather than taking them as props. THROWS on
 * failure — callers own their own error surface.
 */
export async function applyOrgSelection({
  orgIdString,
  orgs,
  navigate,
  shouldRefetchUserConfig = true,
}: ApplyOrgSelectionArgs): Promise<void> {
  const orgsWithCurrent: Org[] = orgs.map((o) =>
    o!.idString === orgIdString
      ? { ...o!, isCurrent: true }
      : { ...o!, isCurrent: false },
  );

  const user = store.get(userAtom);
  if (!user) {
    throw new Error("Missing user for selectOrg");
  }
  const zitadelOrgIdString = store.get(zitadelOrgAtom)?.idString;

  const fetchedUserConfig = shouldRefetchUserConfig
    ? await fetchUserConfig({ user, zitadelOrgIdString })
    : undefined;

  // Before the org flips current, so the access gate finds the new place's
  // payload already cached instead of blanking the layout to fetch it.
  const selectedOrg = orgsWithCurrent.find((o) => o?.isCurrent);
  if (selectedOrg) await prefetchAccess(selectedOrg);

  actAfterLoginSuccess({
    orgs: orgsWithCurrent,
    user,
    userConfig: fetchedUserConfig,
  });
  store.set(authProfileAtom, profileFromZitadelUser(user));

  if (navigate) {
    navigateToMainEntryPageIfSingleOrg(orgsWithCurrent, navigate);
  }
}

export type RefreshCurrentOrgAccessResult =
  | "refreshed"
  | "no-current-org"
  | "current-org-removed";

/**
 * Re-fetch everything the boot caches for the org the user is already in —
 * orgs, user config and the access payload — without changing which org is
 * current and without navigating. This is what a fresh login would load,
 * minus the picker, so a tab coming up
 * on a new build sees the access the server grants today rather than what was
 * cached at the last login.
 *
 * Returns `no-current-org` when nothing is cached to refresh (the boot will
 * fetch from scratch), and `current-org-removed` when the server no longer
 * lists the cached org — the caller sends the user back through the picker.
 * THROWS on request failure; callers own their own error surface.
 */
export async function refreshCurrentOrgAccess(): Promise<RefreshCurrentOrgAccessResult> {
  const currentOrg = store.get(orgsAtom)?.find((o) => o?.isCurrent);
  const user = store.get(userAtom);
  if (!currentOrg || !user) return "no-current-org";
  const zitadelOrgIdString = store.get(zitadelOrgAtom)?.idString;

  const fetchedOrgs = await fetchUserOrgs({ user, zitadelOrgIdString });
  if (!fetchedOrgs.some((o) => o?.idString === currentOrg.idString)) {
    return "current-org-removed";
  }

  await applyOrgSelection({
    orgIdString: currentOrg.idString,
    orgs: fetchedOrgs,
  });
  return "refreshed";
}
